import axios from 'axios'
import Vue from 'vue'
import UtilService from '../services/util.js'

export const CUSTOMER_DEFAULTS = {
    // Hardcoded in the UI via NCS-3425 when route retention was hardcoded at 36 on the backend for tuningSNR
    // Added TUNING_STATS to support NCS-4032, which keeps tuning stat data for 365 days, maybe replace with variable sent up from server later
    dataRetentionDays: {
        CLIENT_ROUTES: 30,
        TUNING_STATS: 366
    }
}

const CustomerService = {
    customerServiceUrlV1: '/api/customers/v1',
    customerServiceUrlV2: '/api/customers/v2',
    securityServiceUrl: '/api/sensus-auth-provider/v1',
    searchTaskUrl: '/api/customers/v1/tasks/search',
    filterValuesUrl: '/api/customers/v1/tasks/filtervalues',
    tasksToStart: '/api/customers/v1/tasks/start',
    tasksUpdate: '/api/customers/v1/tasks',
    customerTuningUrl: '/nettune/features',

    getCustomer (customerName) {
        return axios.get(this.customerServiceUrlV1 + "/customer/" + customerName).then(result => {
            if (result.data.records && result.data.records.length) {
                return result.data.records[0]
            } else {
                // For now return empty object - xylemAdmin does not have a real customer
                return {}
            }
        }).catch(error => {
            console.error('CustomerService.getCustomer error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    getLoggedInUsersCustomer () {
        return axios.get(CustomerService.customerServiceUrlV1).then(result => {
            if (result.data.records && result.data.records.length) {
                Vue.prototype.$customer = result.data.records[0]
            } else {
                // For now return empty object - xylemAdmin does not have a real customer
                Vue.prototype.$customer = {}
            }
            return
        }).catch(error => {
            console.error('CustomerService.getLoggedInUsersCustomer error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    getCustomers(data = {}) {
        return axios({
            url: this.customerServiceUrlV2 + '/search',
            method: 'POST',
            data: {
                ...data,
                page: 1,
                perPage: 1000,
            }
        }).then(result => {
            return result.data.records
        }).catch(error => {
            console.error('CustomerService.getCustomers error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    createCustomer (customer) {
        return axios({
            url: this.customerServiceUrlV1,
            method: 'POST',
            data: customer
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.createCustomer error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    updateCustomer (customer) {

        // If the description or display name is 0 length then set to null so the backend will remove it
        if (customer.description && customer.description.length === 0) {
            customer.description = null;
        }
        if (customer.displayName && customer.displayName.length === 0) {
            customer.displayName = null;
        }

        return axios({
            url: this.customerServiceUrlV1 + "/customer/" + customer.name,
            method: 'PUT',
            data: customer
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.createCustomer error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    deleteCustomer (customerName) {
        return axios({
            url: this.customerServiceUrlV1 + "/customer/" + customerName,
            method: 'DELETE'
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.createCustomer error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    getDiscoveredCustomers () {
        return axios.get(this.customerServiceUrlV1 + '/discovered').then(result => {
            let discovered = result.data.records

            let rniIdList = discovered.map(disc => {
                return disc.rniId
            })

            let rniIdCustomerMap = {};
            discovered.forEach(disc => {
                rniIdCustomerMap[disc.rniId] = disc.rniCustomers.map(cust => {
                    return cust.name
                })
            })

            let rniIdNotOnBoardedCustomerMap = {}
            discovered.forEach(disc => {
                rniIdNotOnBoardedCustomerMap[disc.rniId] = disc.rniCustomers.filter(cust => {
                    return !cust.onBoarded
                }).map(cust => {
                    return cust.name
                })
            })

            return {
                rniIdList: rniIdList,
                rniIdCustomerMap: rniIdCustomerMap,
                rniIdNotOnBoardedCustomerMap: rniIdNotOnBoardedCustomerMap
            }
        }).catch(error => {
            console.error('CustomerService.getDiscoveredCustomers error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    rniFlowControl(updateRNIFlowControlRequests){
        return axios({
            url: this.customerServiceUrlV1 + '/rnis',
            method: 'PUT',
            data: updateRNIFlowControlRequests
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.updateRNIFlowControl error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    getSecurity (customerName) {
        return axios.get(this.securityServiceUrl +"/"+ customerName).then(result => {
            return result && result.data ? result.data : {}
        }).catch(error => {
            console.error('CustomerService.getSecurity error:', error.response ? error.response : error)
            return Promise.reject(error)
        })
    },

    getSecurityForAllCustomers () {
        return axios.get(this.securityServiceUrl).then(result => {
            return result.data.filter(item => {
                return item.client !== null && item.client !== undefined
            })
        }).catch(error => {
            console.error('CustomerService.getSecurityForAllCustomers error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    updateSecurity (security) {
        return axios({
            url: this.securityServiceUrl + '/batch',
            method: 'POST',
            data: security
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.updateSecurity error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    updateNetTuneFeatureModes(customerName, featureMap) {
        return axios({
            url: this.customerServiceUrlV1  + '/' + customerName + this.customerTuningUrl,
            method: 'PUT',
            data: {
                netTuneFeatureModes: featureMap
            }
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.updateSecurity error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    getTask (serviceName, taskName, customer) {
        return axios.get(this.customerServiceUrlV1 + "/tasks/" + serviceName + "/" + taskName + "/" + customer).then(result => {
            if (result.data.records && result.data.records.length) {
                return result.data.records[0]
            }
            return {}
        }).catch(error => {
            console.error('CustomerService.getTask error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    startTasksNow(tasksList) {
        return axios({
            url: this.tasksToStart,
            method: 'PUT',
            data: tasksList
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.startTaskNow error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    enableDisableTasks(tasksList, enableFlag) {
        return axios({
            url: this.tasksUpdate + "?enabled=" + (enableFlag == true ? "true" : "false"),
            method: 'PUT',
            data: tasksList
        }).then(response => {
            return response.data
        }).catch(error => {
            console.error('CustomerService.updateTasks error', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },
    getTasks (taskName) {
        let filterList = undefined
        if (taskName)
        {
            filterList = [{
                filterName: "name",
                fieldName: "name",
                fieldType: "VALUES",
                valueOperator: "EQUALS",
                values: [taskName]
            }]
        }
        return axios({
            url: this.searchTaskUrl,
            method: 'POST',
            data: {
                page: 1,
                perPage: 1000,
                filterList: filterList,
            }
        }).then(result => {
            return result.data.records
        }).catch(error => {
            console.error('CustomerService.getTasks error:', error.response ? error.response : error)
            return Promise.reject(error.response)
        })
    },

    editCommsHubExportStartTime(startTime, customerName) {
        return axios({
            url: this.tasksUpdate + '/radios-tasks/CommsHubExport/'+ customerName,
            method: 'PUT',
            data:{
                "fieldName": "DAILY_START_TIME",
                "fieldType": "LOCAL_TIME",
                "fieldValue": startTime
            }
        })
    }
}
export default CustomerService
